import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import type { PlatformOptions } from '@/types/scheduled-instances';
import type { Channel } from '@/services/channels.service';
import { cn } from '@/utils/cn';
import { Select, Checkbox } from '@/components/ui';
import { usePinterestBoards } from '@/hooks/usePinterestBoards';
import { useLinkedInOrganizations } from '@/hooks/useLinkedInOrganizations';
import { useChannels } from '@/hooks/useChannels';
import { getPlatformDisplayName } from '@/constants/platformLimits';

interface PlatformOptionsSectionProps {
  selectedChannels: Channel[];
  channelOptions: Record<number, PlatformOptions>;
  onOptionsChange: (channelId: number, options: PlatformOptions) => void;
  disabled?: boolean;
  hasVideo?: boolean;
  hasMedia?: boolean;
}

export const PlatformOptionsSection = ({
  selectedChannels,
  channelOptions,
  onOptionsChange,
  disabled,
  hasVideo,
  hasMedia,
}: PlatformOptionsSectionProps) => {
  const { t } = useTranslation('common');
  const [expandedChannels, setExpandedChannels] = useState<Set<number>>(new Set());

  // Platforms that have configurable options
  const platformsWithOptions = new Set([
    'instagram',
    'tiktok',
    'youtube',
    'pinterest',
    'linkedin',
    'facebook',
    'twitter',
    'x',
    'bluesky',
    'threads',
    'google_my_business',
  ]);

  // Group channels by platform, only include platforms with options
  const channelsByPlatform = useMemo(() => {
    const grouped: Record<string, Channel[]> = {};
    for (const channel of selectedChannels) {
      // Convert PlatformType (UPPERCASE) to lowercase for consistency
      const platform = channel.platform.toLowerCase();
      // Skip platforms without options
      if (!platformsWithOptions.has(platform)) continue;
      if (!grouped[platform]) grouped[platform] = [];
      grouped[platform].push(channel);
    }
    return grouped;
  }, [selectedChannels]);

  const toggleChannel = (channelId: number) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return next;
    });
  };

  // Hide section if no channels or no channels with options
  if (selectedChannels.length === 0 || Object.keys(channelsByPlatform).length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {t('platform_options')}
      </label>

      <div className="space-y-2">
        {Object.entries(channelsByPlatform).map(([platform, channels]) => (
          <div key={platform} className="border border-gray-200 rounded-lg overflow-hidden">
            {channels.map((channel) => (
              <div key={channel.id}>
                {/* Channel header - clickable to expand */}
                <button
                  type="button"
                  onClick={() => toggleChannel(channel.id)}
                  disabled={disabled}
                  className={cn(
                    'w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {channel.metadata?.displayName || channel.platformUsername}
                    </span>
                    <span className="text-xs text-gray-500">{getPlatformDisplayName(platform)}</span>
                  </div>
                  <Icon
                    name={expandedChannels.has(channel.id) ? ICONS.ARROW_UP : ICONS.ARROW_DOWN}
                    size="sm"
                    color="text-gray-500"
                  />
                </button>

                {/* Platform-specific options */}
                {expandedChannels.has(channel.id) && (
                  <div className="p-4 bg-white border-t border-gray-200">
                    <PlatformSpecificOptions
                      platform={platform}
                      channelId={channel.id}
                      options={channelOptions[channel.id] || {}}
                      onChange={(opts) => onOptionsChange(channel.id, opts)}
                      disabled={disabled}
                      hasVideo={hasVideo}
                      hasMedia={hasMedia}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Platform-specific options sub-component
interface PlatformSpecificOptionsProps {
  platform: string;
  channelId: number;
  options: PlatformOptions;
  onChange: (options: PlatformOptions) => void;
  disabled?: boolean;
  hasVideo?: boolean;
  hasMedia?: boolean;
}

const PlatformSpecificOptions = ({
  platform,
  channelId,
  options,
  onChange,
  disabled,
  hasVideo,
  hasMedia,
}: PlatformSpecificOptionsProps) => {
  const { t } = useTranslation('common');

  switch (platform) {
    case 'instagram': {
      // Filter options based on media availability
      const instagramOptions = [
        { value: 'FEED', label: t('feed_post') },
        ...(hasVideo ? [{ value: 'REELS', label: t('reel') }] : []),
        { value: 'STORIES', label: t('story') },
      ];

      // Auto-reset if REELS selected but no video
      const currentValue = options.instagram?.contentType || 'FEED';
      const effectiveValue = (!hasVideo && currentValue === 'REELS') ? 'FEED' : currentValue;

      // Update options if auto-reset occurred
      if (effectiveValue !== currentValue) {
        onChange({
          ...options,
          instagram: {
            ...options.instagram,
            contentType: effectiveValue as 'FEED' | 'REELS' | 'STORIES',
          },
        });
      }

      return (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('content_type')}
            </label>
            <Select
              value={effectiveValue}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  instagram: {
                    ...options.instagram,
                    contentType: value as 'FEED' | 'REELS' | 'STORIES',
                  },
                })
              }
              disabled={disabled}
              options={instagramOptions}
              className="w-full"
            />
            {!hasVideo && (
              <p className="text-xs text-gray-500 mt-1">
                {t('reels_require_video', { defaultValue: 'Reels require video content. Add a video to enable Reels.' })}
              </p>
            )}
          </div>
          {effectiveValue === 'REELS' && (
            <Checkbox
              id={`share-feed-${channelId}`}
              checked={options.instagram?.shareReelsFeed ?? true}
              onCheckedChange={(checked) =>
                onChange({
                  ...options,
                  instagram: { ...options.instagram, shareReelsFeed: checked },
                })
              }
              disabled={disabled}
              label={t('share_reel_to_feed')}
            />
          )}
        </div>
      );
    }

    case 'tiktok':
      return (
        <div className="space-y-3">
          {/* Content Disclosure - Video only (not supported for photo posts) */}
          {hasVideo && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs font-medium text-amber-800 mb-2">
                {t('content_disclosure', { defaultValue: 'Content Disclosure' })}
                <span className="ml-1 text-amber-600">({t('required', { defaultValue: 'Required' })})</span>
              </p>
              <p className="text-xs text-amber-700 mb-2">
                {t('tiktok_disclosure_info', { defaultValue: 'If this content promotes a brand, product, or service, you must disclose it.' })}
              </p>
              <div className="space-y-3">
                <Checkbox
                  id={`tiktok-brand-content-${channelId}`}
                  checked={options.tiktok?.brandContentToggle || false}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...options,
                      tiktok: {
                        ...options.tiktok,
                        brandContentToggle: checked,
                      },
                    })
                  }
                  disabled={disabled}
                  label={`${t('branded_content', { defaultValue: 'Branded Content' })} (${t('paid_partnership', { defaultValue: 'Paid partnership with another brand' })})`}
                />
                <Checkbox
                  id={`tiktok-brand-organic-${channelId}`}
                  checked={options.tiktok?.brandOrganicToggle || false}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...options,
                      tiktok: {
                        ...options.tiktok,
                        brandOrganicToggle: checked,
                      },
                    })
                  }
                  disabled={disabled}
                  label={`${t('your_brand', { defaultValue: 'Your Brand' })} (${t('promoting_own_brand', { defaultValue: 'Promoting your own brand/business' })})`}
                />
              </div>
            </div>
          )}

          {/* Video Cover Timestamp - Video only */}
          {hasVideo && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('video_cover_timestamp', { defaultValue: 'Video Cover Timestamp' })}
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={(options.tiktok?.videoCoverTimestampMs || 1000) / 1000}
                onChange={(e) =>
                  onChange({
                    ...options,
                    tiktok: {
                      ...options.tiktok,
                      videoCoverTimestampMs: Math.round(parseFloat(e.target.value) * 1000),
                    },
                  })
                }
                disabled={disabled}
                placeholder="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('video_cover_hint', { defaultValue: 'Choose which second of the video to use as the cover image (default: 1)' })}
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('who_can_view')}
            </label>
            <Select
              value={options.tiktok?.privacyLevel || 'PUBLIC_TO_EVERYONE'}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  tiktok: {
                    ...options.tiktok,
                    privacyLevel: value as
                      | 'PUBLIC_TO_EVERYONE'
                      | 'MUTUAL_FOLLOW_FRIENDS'
                      | 'FOLLOWER_OF_CREATOR'
                      | 'SELF_ONLY',
                  },
                })
              }
              disabled={disabled}
              options={[
                { value: 'PUBLIC_TO_EVERYONE', label: t('public') },
                { value: 'MUTUAL_FOLLOW_FRIENDS', label: t('mutual_follow_friends') },
                { value: 'FOLLOWER_OF_CREATOR', label: t('followers') },
                { value: 'SELF_ONLY', label: t('private') },
              ]}
              className="w-full"
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {/* Comments - available for both video and photo */}
            <Checkbox
              id={`tiktok-comments-${channelId}`}
              checked={!options.tiktok?.disableComment}
              onCheckedChange={(checked) =>
                onChange({
                  ...options,
                  tiktok: { ...options.tiktok, disableComment: !checked },
                })
              }
              disabled={disabled}
              label={t('allow_comments')}
              inline
            />

            {/* Duet & Stitch - Video only */}
            {hasVideo && (
              <>
                <Checkbox
                  id={`tiktok-duet-${channelId}`}
                  checked={!options.tiktok?.disableDuet}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...options,
                      tiktok: { ...options.tiktok, disableDuet: !checked },
                    })
                  }
                  disabled={disabled}
                  label={t('allow_duet')}
                  inline
                />
                <Checkbox
                  id={`tiktok-stitch-${channelId}`}
                  checked={!options.tiktok?.disableStitch}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...options,
                      tiktok: { ...options.tiktok, disableStitch: !checked },
                    })
                  }
                  disabled={disabled}
                  label={t('allow_stitch')}
                  inline
                />
              </>
            )}
          </div>
        </div>
      );

    case 'youtube':
      return (
        <div className="space-y-3">
          {/* Video Title with character counter */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-medium text-gray-600">
                {t('video_title')}
              </label>
              <span className={cn(
                'text-xs',
                (options.youtube?.title?.length || 0) > 100 ? 'text-red-500 font-medium' : 'text-gray-400'
              )}>
                {options.youtube?.title?.length || 0}/100
              </span>
            </div>
            <input
              type="text"
              value={options.youtube?.title || ''}
              onChange={(e) =>
                onChange({
                  ...options,
                  youtube: { ...options.youtube, title: e.target.value },
                })
              }
              disabled={disabled}
              maxLength={100}
              placeholder={t('enter_video_title')}
              className={cn(
                'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                (options.youtube?.title?.length || 0) > 100 ? 'border-red-300' : 'border-gray-300'
              )}
            />
          </div>

          {/* Video Description with character counter */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-xs font-medium text-gray-600">
                {t('description', { defaultValue: 'Description' })}
              </label>
              <span className={cn(
                'text-xs',
                (options.youtube?.description?.length || 0) > 5000 ? 'text-red-500 font-medium' : 'text-gray-400'
              )}>
                {options.youtube?.description?.length || 0}/5,000
              </span>
            </div>
            <textarea
              value={options.youtube?.description || ''}
              onChange={(e) =>
                onChange({
                  ...options,
                  youtube: { ...options.youtube, description: e.target.value },
                })
              }
              disabled={disabled}
              maxLength={5000}
              rows={3}
              placeholder={t('enter_video_description', { defaultValue: 'Enter video description' })}
              className={cn(
                'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none',
                (options.youtube?.description?.length || 0) > 5000 ? 'border-red-300' : 'border-gray-300'
              )}
            />
          </div>

          {/* Privacy Status */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('privacy')}
            </label>
            <Select
              value={options.youtube?.privacyStatus || 'public'}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  youtube: {
                    ...options.youtube,
                    privacyStatus: value as 'public' | 'private' | 'unlisted',
                  },
                })
              }
              disabled={disabled}
              options={[
                { value: 'public', label: t('public') },
                { value: 'unlisted', label: t('unlisted') },
                { value: 'private', label: t('private') },
              ]}
              className="w-full"
            />
          </div>

          {/* YouTube Shorts toggle */}
          <Checkbox
            id={`youtube-short-${channelId}`}
            checked={options.youtube?.isShort || false}
            onCheckedChange={(checked) =>
              onChange({
                ...options,
                youtube: { ...options.youtube, isShort: checked },
              })
            }
            disabled={disabled}
            label={t('youtube_short', { defaultValue: 'Post as YouTube Short' })}
          />
        </div>
      );

    case 'pinterest':
      return (
        <PinterestOptions
          channelId={channelId}
          options={options}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'linkedin':
      return (
        <LinkedInOptions
          channelId={channelId}
          options={options}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'facebook': {
      // Filter options based on media availability
      const facebookOptions = [
        { value: 'FEED', label: t('feed_post') },
        ...(hasVideo ? [{ value: 'REELS', label: t('reel') }] : []),
        { value: 'STORIES', label: t('story') },
      ];

      // Auto-reset if REELS selected but no video
      const currentValue = options.facebook?.contentType || 'FEED';
      const effectiveValue = (!hasVideo && currentValue === 'REELS') ? 'FEED' : currentValue;

      // Update options if auto-reset occurred
      if (effectiveValue !== currentValue) {
        onChange({
          ...options,
          facebook: {
            ...options.facebook,
            contentType: effectiveValue as 'FEED' | 'REELS' | 'STORIES',
          },
        });
      }

      return (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('content_type')}
            </label>
            <Select
              value={effectiveValue}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  facebook: {
                    ...options.facebook,
                    contentType: value as 'FEED' | 'REELS' | 'STORIES',
                  },
                })
              }
              disabled={disabled}
              options={facebookOptions}
              className="w-full"
            />
            {!hasVideo && (
              <p className="text-xs text-gray-500 mt-1">
                {t('reels_require_video', { defaultValue: 'Reels require video content. Add a video to enable Reels.' })}
              </p>
            )}
          </div>
        </div>
      );
    }

    case 'twitter':
    case 'x':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('who_can_reply')}
            </label>
            <Select
              value={options.twitter?.replySettings || 'everyone'}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  twitter: {
                    ...options.twitter,
                    replySettings: value as 'everyone' | 'following' | 'mentioned',
                  },
                })
              }
              disabled={disabled}
              options={[
                { value: 'everyone', label: t('everyone') },
                { value: 'following', label: t('people_you_follow') },
                { value: 'mentioned', label: t('only_mentioned') },
              ]}
              className="w-full"
            />
          </div>
        </div>
      );

    case 'bluesky':
      return (
        <div className="space-y-3">
          {/* Language selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('post_language', { defaultValue: 'Post Language' })}
            </label>
            <Select
              value={options.bluesky?.languages?.[0] || ''}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  bluesky: {
                    ...options.bluesky,
                    languages: value ? [value] : undefined,
                  },
                })
              }
              disabled={disabled}
              options={[
                { value: '', label: t('auto_detect', { defaultValue: 'Auto-detect' }) },
                { value: 'en', label: 'English' },
                { value: 'de', label: 'Deutsch' },
                { value: 'fr', label: 'Français' },
                { value: 'es', label: 'Español' },
                { value: 'pt', label: 'Português' },
                { value: 'it', label: 'Italiano' },
                { value: 'nl', label: 'Nederlands' },
                { value: 'ja', label: '日本語' },
                { value: 'ko', label: '한국어' },
                { value: 'zh', label: '中文' },
                { value: 'ar', label: 'العربية' },
                { value: 'hi', label: 'हिन्दी' },
                { value: 'tr', label: 'Türkçe' },
                { value: 'ru', label: 'Русский' },
                { value: 'pl', label: 'Polski' },
              ]}
              className="w-full"
            />
          </div>

          {/* Content warning */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('content_warning', { defaultValue: 'Content Warning' })}
            </label>
            <Select
              value={options.bluesky?.contentWarning || ''}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  bluesky: {
                    ...options.bluesky,
                    contentWarning: (value || undefined) as 'porn' | 'sexual' | 'nudity' | 'graphic-media' | undefined,
                  },
                })
              }
              disabled={disabled}
              options={[
                { value: '', label: t('none', { defaultValue: 'None' }) },
                { value: 'sexual', label: t('cw_suggestive', { defaultValue: 'Suggestive' }) },
                { value: 'nudity', label: t('cw_nudity', { defaultValue: 'Nudity' }) },
                { value: 'porn', label: t('cw_adult', { defaultValue: 'Adult Content' }) },
                { value: 'graphic-media', label: t('cw_graphic', { defaultValue: 'Graphic Media' }) },
              ]}
              className="w-full"
            />
          </div>

          {/* Link card - only for text-only posts (Bluesky allows one embed type) */}
          {!hasMedia && (
            <>
              <p className="text-sm text-gray-600">{t('link_card_optional')}</p>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('link_url')}
                </label>
                <input
                  type="url"
                  value={options.bluesky?.linkCard?.url || ''}
                  onChange={(e) =>
                    onChange({
                      ...options,
                      bluesky: {
                        ...options.bluesky,
                        linkCard: e.target.value
                          ? { ...options.bluesky?.linkCard, url: e.target.value }
                          : undefined,
                      },
                    })
                  }
                  disabled={disabled}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </>
          )}
        </div>
      );

    case 'threads':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('who_can_reply', { defaultValue: 'Who can reply' })}
            </label>
            <Select
              value={options.threads?.replyControl || 'everyone'}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  threads: {
                    ...options.threads,
                    replyControl: value as 'everyone' | 'accounts_you_follow' | 'mentioned_only',
                  },
                })
              }
              disabled={disabled}
              options={[
                { value: 'everyone', label: t('everyone', { defaultValue: 'Everyone' }) },
                { value: 'accounts_you_follow', label: t('accounts_you_follow', { defaultValue: 'Accounts you follow' }) },
                { value: 'mentioned_only', label: t('mentioned_only', { defaultValue: 'Mentioned only' }) },
              ]}
              className="w-full"
            />
          </div>
        </div>
      );

    case 'google_my_business':
      return (
        <GMBOptions
          options={options}
          onChange={onChange}
          disabled={disabled}
        />
      );

    default:
      // Should not reach here since we filter platforms without options
      return null;
  }
};

// Pinterest-specific options component (needs its own hook call)
interface PinterestOptionsProps {
  channelId: number;
  options: PlatformOptions;
  onChange: (options: PlatformOptions) => void;
  disabled?: boolean;
}

const PinterestOptions = ({
  channelId,
  options,
  onChange,
  disabled,
}: PinterestOptionsProps) => {
  const { t } = useTranslation('common');
  const { data: boards, isLoading: isLoadingBoards, error: boardsError, errorMessage, needsReconnect } = usePinterestBoards(channelId);

  return (
    <div className="space-y-3">
      {/* Board Selector */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('board', { defaultValue: 'Board' })}{' '}
          <span className="text-red-500">*</span>
        </label>
        {isLoadingBoards ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
            <Icon name={ICONS.SPINNER} size="sm" className="animate-spin" />
            {t('loading_boards', { defaultValue: 'Loading boards...' })}
          </div>
        ) : boardsError ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{errorMessage}</p>
            {needsReconnect && (
              <a
                href="/settings/channels"
                className="inline-block mt-2 text-sm text-primary-600 hover:text-primary-700 underline"
              >
                {t('go_to_channels', { defaultValue: 'Go to Channels Settings →' })}
              </a>
            )}
          </div>
        ) : boards && boards.length > 0 ? (
          <>
            <Select
              value={options.pinterest?.boardId || ''}
              onValueChange={(value) =>
                onChange({
                  ...options,
                  pinterest: { ...options.pinterest, boardId: value },
                })
              }
              disabled={disabled}
              options={[
                { value: '', label: t('select_board', { defaultValue: 'Select a board' }) },
                ...boards.map((board) => ({
                  value: board.id,
                  label: `${board.name}${board.privacy === 'SECRET' ? ' 🔒' : ''}`,
                })),
              ]}
              className="w-full"
            />
            {!options.pinterest?.boardId && (
              <p className="mt-1 text-xs text-red-500">
                {t('pinterest_board_required', { defaultValue: 'Required — pick a board before scheduling' })}
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 italic">
            {t('no_boards_found', { defaultValue: 'No boards found. Create a board on Pinterest first.' })}
          </p>
        )}
      </div>

      {/* Pin Title with character counter */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="block text-xs font-medium text-gray-600">
            {t('pin_title')}
          </label>
          <span className={cn(
            'text-xs',
            (options.pinterest?.title?.length || 0) > 100 ? 'text-red-500 font-medium' : 'text-gray-400'
          )}>
            {options.pinterest?.title?.length || 0}/100
          </span>
        </div>
        <input
          type="text"
          value={options.pinterest?.title || ''}
          onChange={(e) =>
            onChange({
              ...options,
              pinterest: { ...options.pinterest, title: e.target.value },
            })
          }
          disabled={disabled}
          maxLength={100}
          placeholder={t('enter_pin_title')}
          className={cn(
            'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            (options.pinterest?.title?.length || 0) > 100 ? 'border-red-300' : 'border-gray-300'
          )}
        />
      </div>

      {/* Pin Description with character counter */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="block text-xs font-medium text-gray-600">
            {t('description', { defaultValue: 'Description' })}
          </label>
          <span className={cn(
            'text-xs',
            (options.pinterest?.description?.length || 0) > 500 ? 'text-red-500 font-medium' : 'text-gray-400'
          )}>
            {options.pinterest?.description?.length || 0}/500
          </span>
        </div>
        <textarea
          value={options.pinterest?.description || ''}
          onChange={(e) =>
            onChange({
              ...options,
              pinterest: { ...options.pinterest, description: e.target.value },
            })
          }
          disabled={disabled}
          maxLength={500}
          rows={3}
          placeholder={t('enter_pin_description', { defaultValue: 'Enter pin description' })}
          className={cn(
            'w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none',
            (options.pinterest?.description?.length || 0) > 500 ? 'border-red-300' : 'border-gray-300'
          )}
        />
      </div>

      {/* Destination URL */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('destination_url')}
        </label>
        <input
          type="url"
          value={options.pinterest?.link || ''}
          onChange={(e) =>
            onChange({
              ...options,
              pinterest: { ...options.pinterest, link: e.target.value },
            })
          }
          disabled={disabled}
          placeholder="https://..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
    </div>
  );
};

// LinkedIn-specific options component with organization mention helper
interface LinkedInOptionsProps {
  channelId: number;
  options: PlatformOptions;
  onChange: (options: PlatformOptions) => void;
  disabled?: boolean;
}

const LINKEDIN_MENTIONS_STORAGE_KEY = 'linkedin_recent_mentions';
const MAX_RECENT_MENTIONS = 10;

interface LinkedInMention {
  name: string;
  urn: string;
  mentionText: string;
  type: 'organization' | 'person';
}

const LinkedInOptions = ({
  channelId,
  options,
  onChange,
  disabled,
}: LinkedInOptionsProps) => {
  const { t } = useTranslation('common');
  const { data: channels } = useChannels();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMentions, setSelectedMentions] = useState<LinkedInMention[]>([]);
  const [recentMentions, setRecentMentions] = useState<LinkedInMention[]>([]);
  const [urlParseSuccess, setUrlParseSuccess] = useState<string | null>(null);

  // Check if this is a LinkedIn organization page
  const channel = channels?.find(c => c.id === channelId);
  const isOrgPage = channel?.metadata?.type === 'organization' ||
                    channel?.platformUserId?.includes('urn:li:organization:');

  // Helper function to parse LinkedIn URLs
  const parseLinkedInUrl = useCallback((input: string): { type: 'organization' | 'person'; vanityName: string } | null => {
    const companyMatch = input.match(/linkedin\.com\/company\/([^/?#]+)/i);
    if (companyMatch) {
      return { type: 'organization', vanityName: companyMatch[1] };
    }
    const personMatch = input.match(/linkedin\.com\/in\/([^/?#]+)/i);
    if (personMatch) {
      return { type: 'person', vanityName: personMatch[1] };
    }
    return null;
  }, []);

  // Use the custom hook for organization search
  const { organizations, isSearching, error } = useLinkedInOrganizations(channelId, searchQuery);

  // Auto-correct visibility for organization pages (can only post publicly)
  useEffect(() => {
    if (isOrgPage && options.linkedin?.visibility === 'CONNECTIONS') {
      onChange({
        ...options,
        linkedin: { ...options.linkedin, visibility: 'PUBLIC' },
      });
    }
  }, [isOrgPage, options, onChange]);

  // Load recent mentions from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LINKEDIN_MENTIONS_STORAGE_KEY);
      if (stored) {
        setRecentMentions(JSON.parse(stored));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Auto-add organization when URL search finds a match
  useEffect(() => {
    if (organizations && organizations.length === 1) {
      const parsed = parseLinkedInUrl(searchQuery);
      if (parsed?.type === 'organization') {
        // Auto-add the single organization found
        addMention(organizations[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations]);

  // Save mention to recent mentions
  const saveMention = useCallback((mention: LinkedInMention) => {
    const updated = [mention, ...recentMentions.filter(m => m.urn !== mention.urn)].slice(0, MAX_RECENT_MENTIONS);
    setRecentMentions(updated);
    try {
      localStorage.setItem(LINKEDIN_MENTIONS_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Ignore localStorage errors
    }
  }, [recentMentions]);

  // Add mention to selected list (uses URN from API response)
  const addMention = useCallback((org: { id?: string; name: string; vanityName: string; urn?: string; mentionText?: string }) => {
    // Use URN from API if available, otherwise construct from ID or vanity name
    const urn = org.urn || (org.id ? `urn:li:organization:${org.id}` : `urn:li:organization:${org.vanityName}`);
    const mentionText = org.mentionText || `@[${org.name}](${urn})`;

    const mention: LinkedInMention = {
      name: org.name,
      urn,
      mentionText,
      type: 'organization',
    };

    setSelectedMentions((prev) => {
      if (prev.some(m => m.urn === mention.urn)) return prev;
      return [...prev, mention];
    });

    saveMention(mention);
    setSearchQuery('');
    setUrlParseSuccess(`Successfully added: ${org.name}`);

    // Clear success message after 3 seconds
    setTimeout(() => setUrlParseSuccess(null), 3000);
  }, [saveMention]);

  // Remove mention from selected list
  const removeMention = useCallback((urn: string) => {
    setSelectedMentions((prev) => prev.filter(m => m.urn !== urn));
  }, []);

  // Copy mention text to clipboard
  const copyMention = useCallback((mention: LinkedInMention) => {
    navigator.clipboard.writeText(mention.mentionText);
  }, []);

  // Remove from recent mentions
  const removeFromRecent = useCallback((urn: string) => {
    const updated = recentMentions.filter(m => m.urn !== urn);
    setRecentMentions(updated);
    try {
      localStorage.setItem(LINKEDIN_MENTIONS_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Ignore localStorage errors
    }
  }, [recentMentions]);

  // Handle search query change with URL detection
  const handleSearchQueryChange = useCallback((value: string) => {
    setSearchQuery(value);
    setUrlParseSuccess(null);

    // Check if input is a LinkedIn URL
    const parsed = parseLinkedInUrl(value);
    if (parsed) {
      if (parsed.type === 'organization') {
        // For organization URLs, trigger search with vanity name
        setSearchQuery(parsed.vanityName);
      } else if (parsed.type === 'person') {
        // For person URLs, show a note that we can't search people
        // Create a manual mention (note: won't actually work for tagging without person ID)
        const mention: LinkedInMention = {
          name: parsed.vanityName,
          urn: `urn:li:person:${parsed.vanityName}`,
          mentionText: `@[${parsed.vanityName}](urn:li:person:${parsed.vanityName})`,
          type: 'person',
        };

        setSelectedMentions((prev) => {
          if (prev.some(m => m.urn === mention.urn)) return prev;
          return [...prev, mention];
        });

        saveMention(mention);
        setSearchQuery('');
        setUrlParseSuccess('Person mention added (Note: LinkedIn person mentions require special API permissions and may not work)');

        // Clear success message after 5 seconds
        setTimeout(() => setUrlParseSuccess(null), 5000);
      }
    }
  }, [parseLinkedInUrl, saveMention]);

  return (
    <div className="space-y-4">
      {/* Visibility */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('visibility')}
        </label>
        <Select
          value={options.linkedin?.visibility || 'PUBLIC'}
          onValueChange={(value) =>
            onChange({
              ...options,
              linkedin: { ...options.linkedin, visibility: value as 'PUBLIC' | 'CONNECTIONS' },
            })
          }
          disabled={disabled}
          options={
            isOrgPage
              ? [{ value: 'PUBLIC', label: t('public') }]
              : [
                  { value: 'PUBLIC', label: t('public') },
                  { value: 'CONNECTIONS', label: t('connections_only') },
                ]
          }
          className="w-full"
        />
        {isOrgPage && (
          <p className="text-xs text-gray-400 mt-1">
            {t('org_pages_public_only', { defaultValue: 'Organization pages can only post publicly' })}
          </p>
        )}
      </div>

      {/* First Comment */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('first_comment', { defaultValue: 'First Comment' })}
        </label>
        <textarea
          value={options.linkedin?.firstComment || ''}
          onChange={(e) =>
            onChange({
              ...options,
              linkedin: { ...options.linkedin, firstComment: e.target.value || undefined },
            })
          }
          disabled={disabled}
          rows={2}
          placeholder={t('first_comment_placeholder', { defaultValue: 'Add a comment that will be posted right after publishing' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('first_comment_hint', { defaultValue: 'This comment will be automatically posted on your LinkedIn post' })}
        </p>
      </div>

      {/* Mention Organizations */}
      <div className="border-t border-gray-200 pt-4">
        <label className="block text-xs font-medium text-gray-600 mb-2">
          {t('mention_organizations', { defaultValue: 'Mention Organizations (to tag in caption)' })}
        </label>

        {/* URL Detection Hint */}
        <p className="text-xs text-gray-500 mb-2">
          💡 {t('linkedin_url_hint', { defaultValue: 'Tip: Paste a LinkedIn company URL (e.g., linkedin.com/company/microsoft) for exact matches' })}
        </p>

        {/* Search Input */}
        <div className="relative mb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchQueryChange(e.target.value)}
            disabled={disabled}
            placeholder={t('linkedin_search_placeholder', { defaultValue: 'Paste LinkedIn company URL or enter exact vanity name (e.g., "microsoft")' })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {isSearching && (
            <div className="absolute right-3 top-2.5">
              <Icon name={ICONS.SPINNER} size="sm" className="animate-spin text-gray-400" />
            </div>
          )}
        </div>

        {/* Success Message */}
        {urlParseSuccess && (
          <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700 flex items-start gap-2">
            <Icon name={ICONS.CHECK} size="xs" className="text-green-600 flex-shrink-0 mt-0.5" />
            <span>{urlParseSuccess}</span>
          </div>
        )}

        {/* Search Results Dropdown */}
        {searchQuery.length >= 3 && organizations && organizations.length > 0 && (
          <div className="mb-3 border border-gray-200 rounded-lg bg-white shadow-sm max-h-48 overflow-y-auto">
            {organizations.map((org) => (
              <button
                key={org.urn || org.vanityName}
                type="button"
                onClick={() => addMention(org)}
                disabled={disabled}
                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
              >
                {org.logo ? (
                  <img
                    src={org.logo}
                    alt={org.name}
                    className="w-8 h-8 rounded flex-shrink-0 object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <Icon name={ICONS.USERS} size="sm" className="text-gray-400" />
                  </div>
                )}
                <span className="text-sm font-medium text-gray-900">{org.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
            {error}
          </div>
        )}

        {/* No Results */}
        {searchQuery.length >= 3 && !isSearching && organizations && organizations.length === 0 && !error && (
          <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500">
            {t('no_organizations_found', { defaultValue: 'No organizations found. Try a different search term.' })}
          </div>
        )}

        {/* Selected Mentions (Chips) */}
        {selectedMentions.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">
              {t('selected_mentions', { defaultValue: 'Selected (click to copy)' })}
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedMentions.map((mention) => (
                <button
                  key={mention.urn}
                  type="button"
                  onClick={() => copyMention(mention)}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-soft-mint text-dark-teal rounded-full text-sm hover:bg-light-teal-gray transition-colors group"
                >
                  <span>@{mention.name}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMention(mention.urn);
                    }}
                    className="hover:text-gray-900"
                  >
                    <Icon name={ICONS.CLOSE} size="xs" />
                  </button>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Helper Text */}
        <p className="text-xs text-gray-500">
          {t('mention_helper_text', { defaultValue: 'Click a mention to copy, then paste in your caption' })}
        </p>

        {/* Recent Mentions */}
        {recentMentions.length > 0 && (
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              {t('recent_mentions', { defaultValue: 'Recent Mentions' })}
            </label>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {recentMentions.map((mention) => (
                <div
                  key={mention.urn}
                  className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded text-sm group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Icon
                      name={mention.type === 'person' ? ICONS.USER : ICONS.USERS}
                      size="xs"
                      className="text-gray-400 flex-shrink-0"
                    />
                    <span className="truncate">{mention.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => copyMention(mention)}
                      className="inline-flex items-center justify-center p-1 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] text-gray-400 hover:text-primary-600 transition-colors"
                      aria-label={t('copy', { defaultValue: 'Copy' })}
                      title={t('copy', { defaultValue: 'Copy' })}
                    >
                      <Icon name={ICONS.COPY} size="xs" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeFromRecent(mention.urn)}
                      className="inline-flex items-center justify-center p-1 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
                      aria-label={t('remove', { defaultValue: 'Remove' })}
                      title={t('remove', { defaultValue: 'Remove' })}
                    >
                      <Icon name={ICONS.CLOSE} size="xs" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// GMB-specific options component
interface GMBOptionsProps {
  options: PlatformOptions;
  onChange: (options: PlatformOptions) => void;
  disabled?: boolean;
}

const GMBOptions = ({
  options,
  onChange,
  disabled,
}: GMBOptionsProps) => {
  const { t } = useTranslation('common');
  const topicType = options.googleMyBusiness?.topicType || 'STANDARD';

  return (
    <div className="space-y-3">
      {/* Post Type */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('post_type', { defaultValue: 'Post Type' })}
        </label>
        <Select
          value={topicType}
          onValueChange={(value) =>
            onChange({
              ...options,
              googleMyBusiness: {
                ...options.googleMyBusiness,
                topicType: value as 'STANDARD' | 'EVENT' | 'OFFER',
                // Clear event/offer when switching types
                ...(value !== 'EVENT' ? { event: undefined } : {}),
                ...(value !== 'OFFER' ? { offer: undefined } : {}),
              },
            })
          }
          disabled={disabled}
          options={[
            { value: 'STANDARD', label: t('standard_post', { defaultValue: 'Standard Post' }) },
            { value: 'EVENT', label: t('event_post', { defaultValue: 'Event' }) },
            { value: 'OFFER', label: t('offer_post', { defaultValue: 'Offer' }) },
          ]}
          className="w-full"
        />
      </div>

      {/* Event Fields */}
      {topicType === 'EVENT' && (
        <div className="space-y-3 p-3 bg-soft-mint border border-light-teal-gray rounded-lg">
          <p className="text-xs font-medium text-dark-teal">
            {t('event_details', { defaultValue: 'Event Details' })}
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('event_title', { defaultValue: 'Event Title' })}
            </label>
            <input
              type="text"
              value={options.googleMyBusiness?.event?.title || ''}
              onChange={(e) =>
                onChange({
                  ...options,
                  googleMyBusiness: {
                    ...options.googleMyBusiness,
                    event: {
                      ...options.googleMyBusiness?.event,
                      title: e.target.value,
                      startDate: options.googleMyBusiness?.event?.startDate || '',
                    },
                  },
                })
              }
              disabled={disabled}
              placeholder={t('enter_event_title', { defaultValue: 'Enter event title' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('start_date', { defaultValue: 'Start Date' })}
              </label>
              <input
                type="date"
                value={options.googleMyBusiness?.event?.startDate || ''}
                onChange={(e) =>
                  onChange({
                    ...options,
                    googleMyBusiness: {
                      ...options.googleMyBusiness,
                      event: {
                        ...options.googleMyBusiness?.event,
                        title: options.googleMyBusiness?.event?.title || '',
                        startDate: e.target.value,
                      },
                    },
                  })
                }
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('end_date', { defaultValue: 'End Date' })}
              </label>
              <input
                type="date"
                value={options.googleMyBusiness?.event?.endDate || ''}
                onChange={(e) =>
                  onChange({
                    ...options,
                    googleMyBusiness: {
                      ...options.googleMyBusiness,
                      event: {
                        ...options.googleMyBusiness?.event,
                        title: options.googleMyBusiness?.event?.title || '',
                        startDate: options.googleMyBusiness?.event?.startDate || '',
                        endDate: e.target.value || undefined,
                      },
                    },
                  })
                }
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('start_time', { defaultValue: 'Start Time' })}
              </label>
              <input
                type="time"
                value={options.googleMyBusiness?.event?.startTime || ''}
                onChange={(e) =>
                  onChange({
                    ...options,
                    googleMyBusiness: {
                      ...options.googleMyBusiness,
                      event: {
                        ...options.googleMyBusiness?.event,
                        title: options.googleMyBusiness?.event?.title || '',
                        startDate: options.googleMyBusiness?.event?.startDate || '',
                        startTime: e.target.value || undefined,
                      },
                    },
                  })
                }
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('end_time', { defaultValue: 'End Time' })}
              </label>
              <input
                type="time"
                value={options.googleMyBusiness?.event?.endTime || ''}
                onChange={(e) =>
                  onChange({
                    ...options,
                    googleMyBusiness: {
                      ...options.googleMyBusiness,
                      event: {
                        ...options.googleMyBusiness?.event,
                        title: options.googleMyBusiness?.event?.title || '',
                        startDate: options.googleMyBusiness?.event?.startDate || '',
                        endTime: e.target.value || undefined,
                      },
                    },
                  })
                }
                disabled={disabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Offer Fields */}
      {topicType === 'OFFER' && (
        <div className="space-y-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs font-medium text-green-800">
            {t('offer_details', { defaultValue: 'Offer Details' })}
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('coupon_code', { defaultValue: 'Coupon Code' })}
            </label>
            <input
              type="text"
              value={options.googleMyBusiness?.offer?.couponCode || ''}
              onChange={(e) =>
                onChange({
                  ...options,
                  googleMyBusiness: {
                    ...options.googleMyBusiness,
                    offer: {
                      ...options.googleMyBusiness?.offer,
                      couponCode: e.target.value || undefined,
                    },
                  },
                })
              }
              disabled={disabled}
              placeholder={t('enter_coupon_code', { defaultValue: 'e.g., SAVE20' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('redeem_url', { defaultValue: 'Redeem Online URL' })}
            </label>
            <input
              type="url"
              value={options.googleMyBusiness?.offer?.redeemOnlineUrl || ''}
              onChange={(e) =>
                onChange({
                  ...options,
                  googleMyBusiness: {
                    ...options.googleMyBusiness,
                    offer: {
                      ...options.googleMyBusiness?.offer,
                      redeemOnlineUrl: e.target.value || undefined,
                    },
                  },
                })
              }
              disabled={disabled}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('terms_conditions', { defaultValue: 'Terms & Conditions' })}
            </label>
            <textarea
              value={options.googleMyBusiness?.offer?.termsConditions || ''}
              onChange={(e) =>
                onChange({
                  ...options,
                  googleMyBusiness: {
                    ...options.googleMyBusiness,
                    offer: {
                      ...options.googleMyBusiness?.offer,
                      termsConditions: e.target.value || undefined,
                    },
                  },
                })
              }
              disabled={disabled}
              rows={2}
              placeholder={t('enter_terms', { defaultValue: 'Enter terms and conditions' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          {t('call_to_action', { defaultValue: 'Call to Action Button' })}
        </label>
        <Select
          value={options.googleMyBusiness?.ctaType || ''}
          onValueChange={(value) =>
            onChange({
              ...options,
              googleMyBusiness: {
                ...options.googleMyBusiness,
                ctaType: (value || undefined) as 'BOOK' | 'ORDER' | 'SHOP' | 'LEARN_MORE' | 'SIGN_UP' | 'GET_OFFER' | 'CALL' | undefined,
              },
            })
          }
          disabled={disabled}
          options={[
            { value: '', label: t('no_cta', { defaultValue: 'No button' }) },
            { value: 'BOOK', label: t('cta_book', { defaultValue: 'Book' }) },
            { value: 'ORDER', label: t('cta_order', { defaultValue: 'Order Online' }) },
            { value: 'SHOP', label: t('cta_shop', { defaultValue: 'Shop' }) },
            { value: 'LEARN_MORE', label: t('cta_learn_more', { defaultValue: 'Learn More' }) },
            { value: 'SIGN_UP', label: t('cta_sign_up', { defaultValue: 'Sign Up' }) },
            { value: 'GET_OFFER', label: t('cta_get_offer', { defaultValue: 'Get Offer' }) },
            { value: 'CALL', label: t('cta_call', { defaultValue: 'Call Now' }) },
          ]}
          className="w-full"
        />
      </div>
      {options.googleMyBusiness?.ctaType && options.googleMyBusiness.ctaType !== 'CALL' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {t('cta_url', { defaultValue: 'Button URL' })}
          </label>
          <input
            type="url"
            value={options.googleMyBusiness?.ctaUrl || ''}
            onChange={(e) =>
              onChange({
                ...options,
                googleMyBusiness: { ...options.googleMyBusiness, ctaUrl: e.target.value },
              })
            }
            disabled={disabled}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      )}
    </div>
  );
};
